import { useState, useMemo } from "react";
import {
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    addToast,
    Chip,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLink,
    faArrowUpRightFromSquare,
    faTrash,
    faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { UserConnection } from "@/types/connection";
import { Platform } from "@/types/enums";
import useConfirm from "@/lib/confirm/useConfirm";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/dashboard/account-settings/connections")(
    {
        component: Connections,
    },
);

type PlatformKey = "JOHN_DEERE" | "CASE_IH" | "CLIMATE_FIELDVIEW" | "FIELDNET";

interface PlatformConfig {
    id: string;
    name: string;
    logo: string;
    authUrl?: string;
    platform: Platform;
}

const PLATFORMS: Record<PlatformKey, PlatformConfig> = {
    JOHN_DEERE: {
        id: "john_deere",
        name: "John Deere",
        logo: "/assets/images/JDOpsCntr_Wordmark_Green.png",
        authUrl: "leaf/jd_auth_url/",
        platform: Platform.JohnDeere,
    },
    CASE_IH: {
        id: "case_ih",
        name: "Case IH",
        logo: "/assets/images/CaseNewHolland_CombinedLogos.png",
        authUrl: "leaf/cnhi_auth_url/",
        platform: Platform.CNHI,
    },
    CLIMATE_FIELDVIEW: {
        id: "climate_fieldview",
        name: "Climate FieldView",
        logo: "/assets/images/Climate_FieldView_Horizontal_thumb.svg",
        authUrl: "leaf/fieldview_auth_url/",
        platform: Platform.FieldView,
    },
    FIELDNET: {
        id: "fieldnet",
        name: "FieldNET",
        logo: "/assets/images/fieldnet-logo.svg",
        authUrl: "connections/fieldnet_auth_url/",
        platform: Platform.FieldNET,
    },
};

function Connections() {
    const [confirmPlatform, setConfirmPlatform] = useState<PlatformKey | null>(
        null,
    );

    const { t } = useTranslation("messages");
    const { useGetRequest, useMutateRequest } = useRequest();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { showConfirm } = useConfirm();

    const connectionQuery = useGetRequest<UserConnection[]>({
        url: `/user_connections/?user=${user?.id}`,
        method: "get",
        enabled: !!user?.id,
    });

    const getPlatformAuthUrl = useMutateRequest<string>({
        method: "get",
        errorMessage: t("error.failed_to_get_authentication_url_for_platform"),
    });

    const handlePlatformAuth = async (platformId: PlatformKey) => {
        getPlatformAuthUrl.mutate(
            {
                url: PLATFORMS[platformId].authUrl,
            },
            {
                onSuccess: (data) => {
                    window.open(data, "_blank");
                },
                onError: () => {
                    addToast({
                        title: t(
                            "addToast.failed_to_get_authentication_url_for_platform",
                            {
                                platformName: PLATFORMS[platformId].name,
                            },
                        ),
                        color: "danger",
                        timeout: 3000,
                    });
                },
            },
        );
    };

    const handleConfirmConnect = async () => {
        if (!confirmPlatform) return;
        await handlePlatformAuth(confirmPlatform);
        setConfirmPlatform(null);
    };

    const connectionData = useMemo(() => {
        return (connectionQuery.data ?? []).reduce(
            (acc, connection) => {
                acc[connection.platform] = connection;
                return acc;
            },
            {} as Record<Platform, UserConnection>,
        );
    }, [connectionQuery.data]);

    const deleteConnection = useMutateRequest({
        method: "delete",
        errorMessage: t("error.failed_to_delete_connection"),
        successMessage: t("success.connection_deleted_successfully"),
        onSuccess: () => {
            connectionQuery.refetch();
        },
    });

    const handleDeleteConnection = async (connection: UserConnection) => {
        showConfirm({
            title: t("connections.delete_connection", { ns: "accountSettings" }),
            description: t("connections.delete_connection_description", { ns: "accountSettings" }),
            dismissable: true,
            color: "danger",
            onConfirm: async () => {
                await deleteConnection.mutateAsync({
                    url: `/user_connections/${connection.id}/`,
                });
            },
        });
    };
    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">{t("connections.title", { ns: "accountSettings" })}</h1>
                <div className="flex items-center gap-2">
                    <Button
                        color="primary"
                        onPress={() => navigate({ to: "/dashboard/add-field" })}
                    >
                        {t("connections.import_field", { ns: "accountSettings" })}
                    </Button>
                    <Button
                        color="primary"
                        isIconOnly
                        onPress={() => connectionQuery.refetch()}
                        isLoading={connectionQuery.isRefetching}
                    >
                        <FontAwesomeIcon icon={faRefresh} />
                    </Button>
                </div>
            </div>

            <div className="space-y-4 mb-8">
                {Object.keys(PLATFORMS).map((key) => {
                    const platformKey = key as PlatformKey;
                    const platform = PLATFORMS[platformKey];
                    const activeConnection = connectionData[platform.platform];
                    return (
                        <div
                            key={platform.id}
                            className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4"
                        >
                            <div className="flex items-center gap-4">
                                <img
                                    src={platform.logo}
                                    alt={platform.name}
                                    className="h-16 w-auto object-contain"
                                    style={{ maxWidth: 180 }}
                                />
                            </div>
                            {activeConnection?.is_pending ? (
                                <div className="flex items-center gap-2">
                                    <Chip color="warning">
                                        {t("connections.pending", { ns: "accountSettings" })}
                                    </Chip>
                                    <Button
                                        isIconOnly
                                        color="danger"
                                        size="sm"
                                        onPress={() =>
                                            handleDeleteConnection(
                                                activeConnection,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            ) : activeConnection ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-green-600 font-semibold">
                                        {t("connections.connected", { ns: "accountSettings" })}
                                    </span>
                                    <Button
                                        isIconOnly
                                        color="danger"
                                        size="sm"
                                        onPress={() =>
                                            handleDeleteConnection(
                                                activeConnection,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    color="primary"
                                    variant="flat"
                                    onPress={() =>
                                        setConfirmPlatform(platformKey)
                                    }
                                    isLoading={getPlatformAuthUrl.isPending}
                                    className="px-6 py-2 text-base font-medium"
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faLink}
                                            className="h-4 w-4"
                                        />
                                    }
                                >
                                    {t("connections.connect", { ns: "accountSettings" })}
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>

            <Modal
                isOpen={!!confirmPlatform}
                onClose={() => setConfirmPlatform(null)}
                size="md"
            >
                <ModalContent>
                    <ModalHeader>
                        {confirmPlatform &&
                            t("connections.connect_to_platform", {
                                ns: "accountSettings",
                                platform: PLATFORMS[confirmPlatform].name,
                            })}
                    </ModalHeader>
                    <ModalBody>
                        <div className="flex items-center gap-4">
                            <FontAwesomeIcon
                                icon={faLink}
                                className="h-8 w-8 text-gray-500"
                            />
                            <span className="text-lg">
                                {confirmPlatform &&
                                    t("connections.taken_to_new_platform", {
                                        ns: "accountSettings",
                                        platform:
                                            PLATFORMS[confirmPlatform].name,
                                    })}
                            </span>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onClick={() => setConfirmPlatform(null)}
                        >
                            {t("connections.cancel", { ns: "accountSettings" })}
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleConfirmConnect}
                            endContent={
                                <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                    className="h-5 w-5"
                                />
                            }
                        >
                            {t("connections.connect", { ns: "accountSettings" })}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
}
